import type { Router, RouteLocationNormalized } from 'vue-router';
import { useUserStoreWithOut } from '@/store/modules/user';
import { useTransitionSetting } from '@/hooks/setting/useTransitionSetting';
import { AxiosCanceler } from '@/utils/http/axios/axiosCancel';
import { Modal, notification } from 'ant-design-vue';
import { warn } from  'xmgoing-utils';
import { unref } from 'vue';
import { setRouteChange } from '@/logics/mitt/routeChange';
import { createStateGuard } from './stateGuard';
import nProgress from 'nprogress';
import projectSetting from '@/settings/projectSetting';
import { createParamMenuGuard } from './paramMenuGuard';
import GoingUtils from "@/utils/goingUtils";
import {dbUtils} from "xmgoing-utils";
import {usePageModelsStore} from "@/store/modules/pageModels";
import {useGridTableStore} from "@/store/modules/gridTableStore";
import NProgress from "nprogress";

// Don't change the order of creation
export function setupRouterGuard(router: Router) {
  createPageGuard(router);
  createPageLoadingGuard(router);
  createHttpGuard(router);
  createScrollGuard(router);
  createMessageGuard(router);
  createProgressGuard(router);
  createParamMenuGuard(router); // must after createPermissionGuard (menu has been built.)
  createStateGuard(router);
}

interface ISearchObj{
  obj:object|undefined|null
}

/**
 * 根据点击路径查询到对应的记录
 * @param userRotes
 * @param toPath
 * @param searchObj
 */
function getCurPathObj(userRotes:Array<any>,toPath:string,searchObj:ISearchObj){
  if(userRotes&&userRotes.filter){
    const selObj=userRotes.filter((roteItem)=>{
      return roteItem.path===toPath;
    });
    if(selObj&&selObj[0]){
      searchObj.obj=selObj[0];
    }else{
      userRotes.forEach((sonItem)=>{
        if(sonItem&&sonItem.children){
          getCurPathObj(sonItem.children,toPath,searchObj)
        }
      })
    }
  }
}

/**
 * Hooks for handling page state
 */
function createPageGuard(router: Router) {
  const loadedPageMap = new Map<string, boolean>();

  router.beforeEach(async (to) => {
    const routeId = to.params.pageCode; // 根据路由参数生成唯一标识
    to.meta.storeId = `${routeId}`; // 确保唯一性
    if(to.meta.storeId&&to.meta.storeId!=='undefined'){
      // 如果存在 routeId，启动加载状态
      NProgress.start();
      usePageModelsStore();
      useGridTableStore();
    }
    // The page has already been loaded, it will be faster to open it again, you don’t need to do loading and other processing
    to.meta.loaded = !!loadedPageMap.get(to.path);
    //表示界面不是静态路由 而是动态路由
    if('PageNotFound'===to.name){
      const userRotes = dbUtils.getUserMenus();
      const searchObj:ISearchObj={obj:null};
      getCurPathObj(userRotes,to.path,searchObj);
      if(searchObj.obj){
        const {name,id}:any=searchObj.obj;
        Object.assign(to.meta, {
          title:name,
          pageId:id,
          hideBreadcrumb: false
        })
      }

    }
    // Notify routing changes
    setRouteChange(to);
    return true;
  });

  router.afterEach((to) => {
    loadedPageMap.set(to.path, true);
  });
}

// Used to handle page loading status
function createPageLoadingGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const { getOpenPageLoading } = useTransitionSetting();
  router.beforeEach(async (to) => {

    if (!userStore.getToken) {
      return true;
    }
    if (to.meta.loaded) {
      return true;
    }

    if (unref(getOpenPageLoading)) {
     GoingUtils.showLoading();
      return true;
    }

    return true;
  });
  router.afterEach(async () => {
    if (unref(getOpenPageLoading)) {
      // TODO Looking for a better way
      // The timer simulates the loading time to prevent flashing too fast,
      setTimeout(() => {
        GoingUtils.closeLoading();
        // appStore.setPageLoading(false);
      }, 220);
    }
    return true;
  });
}

/**
 * The interface used to close the current page to complete the request when the route is switched
 * @param router
 */
function createHttpGuard(router: Router) {
  const { removeAllHttpPending } = projectSetting;
  let axiosCanceler: Nullable<AxiosCanceler>;
  if (removeAllHttpPending) {
    axiosCanceler = new AxiosCanceler();
  }
  router.beforeEach(async () => {
    // Switching the route will delete the previous request
    axiosCanceler?.removeAllPending();
    return true;
  });
}

// Routing switch back to the top
function createScrollGuard(router: Router) {
  const isHash = (href: string) => {
    return /^#/.test(href);
  };

  const body = document.body;

  router.afterEach(async (to) => {
    // scroll top
    isHash((to as RouteLocationNormalized & { href: string })?.href) && body.scrollTo(0, 0);
    return true;
  });
}

/**
 * Used to close the message instance when the route is switched
 * @param router
 */
export function createMessageGuard(router: Router) {
  const { closeMessageOnSwitch } = projectSetting;

  router.beforeEach(async () => {
    try {
      if (closeMessageOnSwitch) {
        Modal.destroyAll();
        notification.destroy();
      }
    } catch (error) {
      warn('message guard error:' + error);
    }
    return true;
  });
}

export function createProgressGuard(router: Router) {
  const { getOpenNProgress } = useTransitionSetting();
  router.beforeEach(async (to) => {
    if (to.meta.loaded) {
      return true;
    }
    unref(getOpenNProgress) && nProgress.start();
    return true;
  });

  router.afterEach(async () => {
    unref(getOpenNProgress) && nProgress.done();
    return true;
  });
}
